/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import {useCallback} from 'react';
import {useMutation, useQueryClient} from 'react-query';
import {replaceDevice} from '@ion/core/src/services/Connect';
import {Device} from '@ion/core/src/types/entities';
import {QueryKey} from '../config/QueryKeys';
import {createMutationResult} from '../utils/helpers';
import {UseDefaultQueryResult} from './types';

export interface UseSwapDeviceParams {
  identifierType?: string;
  identifier: string;
  device: Device;
};

export type UseSwapDeviceResult = UseDefaultQueryResult & {
  save: ({identifier, device, identifierType}: UseSwapDeviceParams) => void;
};

const api = async({identifier, device, identifierType = 'ESN'}: UseSwapDeviceParams) => {
  try {
    const swapped = await replaceDevice(identifier, device, identifierType);
    return swapped;
  } catch (err) {
    throw new Error(`An ${err.status || ''} Error Occurred`);
  }
};

/**
 * The useSwapDevice hook is a generic hook that replace the existing
 * device with new ESN.
 *
 * ```typescript
 *
 *    import useSwapDevice from '@ion/query/src/hooks/';
 *
 *    const {save, isSuccess, isError} = useSwapDevice(deviceId);
 *
 * ```
 */
export const useSwapDevice = (deviceId?: number): UseSwapDeviceResult => {
  const queryClient = useQueryClient();
  const mutation    = useMutation(api, {
    onSettled : () => {
      if (deviceId) {
        queryClient.invalidateQueries([QueryKey.SWAP_DEVICES, deviceId]);
      }
    }
  });
  const save = useCallback(({identifier, device, identifierType}: UseSwapDeviceParams) => {
    mutation.mutate({identifier, device, identifierType});
  }, [mutation]);
  return createMutationResult(
    mutation, {save}
  ) as UseSwapDeviceResult;
};
export default useSwapDevice;
